import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

// mui
import { DialogContent, Grid as MuiGrid, DialogActions } from '@mui/material';
import { makeStyles } from '@mui/styles';

// components
import { TextFieldBase } from '../inputs/TextFieldBase';
import { SimpleSelect } from '../inputs';
import CBButton from '../Buttons/CbButton';
import GrayAWSLogo from '../../_assets/svg/GrayAWSLogo.svg';

// utils
import { withFormController } from '../hocs/forms';
import { PubSub } from '../../utils/eventUtils';
import useNotifications from '../../_lib/notifications/useNotifications';
import { useAPIErrorHandler } from '../hooks/useAPIErrorHandler';

// apis
import {
  getAWSregions,
  connectAWS,
} from '../../policyholders/connectors/ConnectorService';

const TextField = withFormController(TextFieldBase);
const Select = withFormController(SimpleSelect);

const schema = Yup.object().shape({
  accountId: Yup.string().required().label('Account ID'),
  region: Yup.string().required().label('region'),
  accessId: Yup.string().required().label('Access Key ID'),
  secret: Yup.string().required().label('Secret Key ID'),
});

export const AWSConnector = ({ ...props }) => {
  const classes = useClasses();
  const notifications = useNotifications();
  const handleAPIError = useAPIErrorHandler();
  const { handleSubmit, ...methods } = useForm({
    resolver: yupResolver(schema),
  });
  const [options, setOptions] = React.useState([]);

  const {
    formState: { isSubmitting },
  } = methods;

  const customer = useSelector(({ account }) => account);

  React.useEffect(() => {
    getAWSregions()
      .then((response: any) => {
        const regions = Object.keys(response.data);
        const massagedValue = regions.map((region) => ({
          label: region,
          value: region.replaceAll('_', '-'),
        }));

        setOptions(massagedValue);
      })
      .catch(
        handleAPIError('Cannot find AWS Regions, please try again later')
      );
    // eslint-disable-next-line
  }, []);

  const onSubmit = (formData: any) => {
    return connectAWS(
      {},
      {
        connector: 'AWS',
        provider: 'COWBELL',
        credentials: {
          ...formData,
        },
        cbid: parseInt(customer.cbid),
        metadata: null,
      }
    )
      .then(() => {
        notifications.success('AWS account created successfully!');
        // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
        PubSub.publish('connector-page-refetch');
        props.close();
      })
      .catch(handleAPIError('Failed to add the connector'));
  };

  return (
    // @ts-expect-error TS(2741): Property 'handleSubmit' is missing in type '{ chil... Remove this comment to see the full error message
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent classes={{ root: classes.container }}>
          <MuiGrid>
            <p className={classes.description}>
              Please enter your AWS authentication information to activate this
              connector.
            </p>
          </MuiGrid>
          <MuiGrid container spacing={2} justifyContent="center">
            <MuiGrid item md={6}>
              <TextField
                name="accountId"
                placeholder="Please enter account ID"
                label="Account ID"
                fullWidth
                required
                data-qa="accountId"
              />
            </MuiGrid>
            <MuiGrid item md={6}>
              <Select
                label="Region"
                required
                name="region"
                fullWidth
                displayEmpty
                renderValue={renderValue}
                InputLabelProps={{ className: classes.selectLabel }}
                options={options}
              />
            </MuiGrid>
          </MuiGrid>
          <MuiGrid container spacing={2}>
            <MuiGrid item md={6}>
              <TextField
                name="accessId"
                type="password"
                placeholder="Please enter your access key ID"
                label="Access Key ID"
                fullWidth
                required
                data-qa="accessId"
              />
            </MuiGrid>
            <MuiGrid item md={6}>
              <TextField
                name="secret"
                type="password"
                placeholder="Please enter your secret access key"
                label="Secret Access Key"
                fullWidth
                required
                data-qa="secret"
              />
            </MuiGrid>
          </MuiGrid>
        </DialogContent>
        <DialogActions>
          <CBButton
            type="submit"
            styleName="cancel"
            buttonText="Cancel"
            onClick={() => props.close()}
          />
          <CBButton
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            styleName="ctaButton"
            buttonText="Next"
          />
        </DialogActions>
      </form>
    </FormProvider>
  );
};

function renderValue(value: any) {
  return value || 'Please select your region';
}

const useClasses = makeStyles(({ palette, config }) => ({
  selectLabel: {
    paddingTop: '5px',
  },
  email: {
    color: `${palette.email.primary} !important`,
    fontSize: 12,
    marginLeft: '0.75rem',
  },
  container: {
    overflow: 'auto',
    paddingBottom: '1rem',
    color: palette.primary.contrastText,
  },
  description: {
    color: palette.primary.contrastText,
    fontWeight: config.weights.bold,
    lineHeight: 1.17,
    textAlign: 'left',
    fontSize: '1.33333rem',
  },
  header: {
    fontSize: '1.6667rem',
    lineHeight: 0.9,
    textAlign: 'left',
    margin: '2rem 0 0 0',
    color: palette.primary.contrastText,
  },
}));

export const AWSConnectorConfig = {
  AWSConnector: {
    component: AWSConnector,
    config: {
      override: false,
      maxWidth: 'md',
      minWidth: 'sm',
      title: 'Amazon Web Services Authentication',
      icon: <GrayAWSLogo />,
    },
  },
};
